import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// Removed recharts imports - using stacked progress bars instead
import { 
  ArrowLeft, Building2, FileText, AlertTriangle, Shield, CheckCircle, 
  Clock, Users, Activity, TrendingUp, Calendar, Eye, Check
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#6b7280'];
const SEVERITY_COLORS = {
  'Low': '#22c55e',
  'Medium': '#f59e0b', 
  'High': '#f97316',
  'Critical': '#ef4444'
};

const GCSubcontractorAnalytics = ({ user }) => {
  const navigate = useNavigate();
  const { projectId, subCompanyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [acknowledging, setAcknowledging] = useState(null);

  useEffect(() => {
    if (projectId && subCompanyId) {
      fetchAnalytics();
    }
  }, [projectId, subCompanyId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/gc/projects/${projectId}/subcontractors/${subCompanyId}/analytics`
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load subcontractor analytics');
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeJSA = async (jsaId) => {
    setAcknowledging(jsaId);
    try {
      await axiosInstance.post(`/gc/projects/${projectId}/jsas/${jsaId}/acknowledge`);
      toast.success('JSA acknowledged and on file');
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to acknowledge JSA');
    } finally {
      setAcknowledging(null);
    }
  };

  const acknowledgeIncident = async (incidentId, requestCorrective = false) => {
    setAcknowledging(incidentId);
    try {
      await axiosInstance.post(
        `/gc/projects/${projectId}/incidents/${incidentId}/acknowledge?request_corrective_action=${requestCorrective}`
      );
      toast.success(requestCorrective ? 'Incident acknowledged - corrective action requested' : 'Incident acknowledged');
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to acknowledge incident');
    } finally {
      setAcknowledging(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      'Low': 'bg-green-500',
      'Medium': 'bg-yellow-500',
      'High': 'bg-orange-500',
      'Critical': 'bg-red-500'
    };
    return <Badge className={colors[severity] || 'bg-gray-500'}>{severity}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading subcontractor analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/gc/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Card className="mt-6">
            <CardContent className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Analytics data not available</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { analytics: data } = analytics;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/gc/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">{analytics.subCompanyName}</h1>
              </div>
              <p className="text-gray-600">Project: {analytics.projectName}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-orange-600 border-orange-300">
            📊 {analytics.dataLabel}
          </Badge>
        </div>

        {/* Read-Only Notice */}
        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <p className="font-medium text-orange-800">Read-Only View</p>
              <p className="text-sm text-orange-700">
                This data is reported by the subcontractor. You can acknowledge items but cannot edit them.
                No worker medical details are shown.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{data.jsas.total}</p>
              <p className="text-xs text-gray-500">JSAs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{data.incidents.total}</p>
              <p className="text-xs text-gray-500">Incidents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{data.nearMisses.total}</p>
              <p className="text-xs text-gray-500">Near Misses</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Shield className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{data.audits.averageScore}%</p>
              <p className="text-xs text-gray-500">Avg Audit</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{data.workforce.totalWorkers}</p>
              <p className="text-xs text-gray-500">Workers</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Card>
          <CardContent className="p-6">
            <Tabs defaultValue="jsas">
              <TabsList className="mb-4">
                <TabsTrigger value="jsas">JSAs</TabsTrigger>
                <TabsTrigger value="incidents">Incidents</TabsTrigger>
                <TabsTrigger value="audits">Audits</TabsTrigger>
                <TabsTrigger value="bodymap">Body Map</TabsTrigger>
              </TabsList>

              {/* JSAs Tab */}
              <TabsContent value="jsas">
                <div className="space-y-4">
                  {/* JSA Status Distribution - Stacked Progress Bars */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">JSA Status Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const entries = Object.entries(data.jsas.byStatus);
                          const total = entries.reduce((sum, [, val]) => sum + val, 0);
                          return entries.length > 0 ? (
                            <div className="space-y-3">
                              {entries.map(([status, count], idx) => {
                                const percentage = total > 0 ? (count / total) * 100 : 0;
                                return (
                                  <div key={status} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-20 truncate capitalize">{status.replace('_', ' ')}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: COLORS[idx % COLORS.length]
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-8 text-right">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic py-4 text-center">No JSAs recorded</p>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Status Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.entries(data.jsas.byStatus).map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="capitalize text-sm">{status.replace('_', ' ')}</span>
                              <Badge variant="outline">{count}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Recent JSAs */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Recent JSAs</CardTitle>
                      <CardDescription>You can acknowledge JSAs as &quot;on file&quot; - this does not imply approval</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Task</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.jsas.recentJsas?.map((jsa) => (
                            <TableRow key={jsa.id}>
                              <TableCell className="font-medium">{jsa.task}</TableCell>
                              <TableCell>
                                <Badge variant={jsa.status === 'approved' ? 'default' : 'secondary'}>
                                  {jsa.status}
                                </Badge>
                              </TableCell>
                              <TableCell>{formatDate(jsa.date)}</TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => acknowledgeJSA(jsa.id)}
                                  disabled={acknowledging === jsa.id}
                                >
                                  <Check className="w-3 h-3 mr-1" />
                                  {acknowledging === jsa.id ? 'Saving...' : 'Acknowledge'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!data.jsas.recentJsas || data.jsas.recentJsas.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                                No recent JSAs
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Incidents Tab */}
              <TabsContent value="incidents">
                <div className="space-y-4">
                  {/* Incident Charts - Stacked Progress Bars */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Incidents by Severity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const entries = Object.entries(data.incidents.bySeverity);
                          const total = entries.reduce((sum, [, val]) => sum + val, 0);
                          return entries.length > 0 ? (
                            <div className="space-y-3">
                              {entries.map(([severity, count]) => {
                                const percentage = total > 0 ? (count / total) * 100 : 0;
                                return (
                                  <div key={severity} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-16 capitalize">{severity}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: SEVERITY_COLORS[severity] || '#6b7280'
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-8 text-right">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic py-4 text-center">No incidents recorded</p>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Incidents by Type</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const entries = Object.entries(data.incidents.byType);
                          const total = entries.reduce((sum, [, val]) => sum + val, 0);
                          return entries.length > 0 ? (
                            <div className="space-y-3">
                              {entries.map(([type, count], idx) => {
                                const percentage = total > 0 ? (count / total) * 100 : 0;
                                return (
                                  <div key={type} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-24 truncate" title={type}>{type}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: COLORS[idx % COLORS.length]
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-8 text-right">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic py-4 text-center">No incidents recorded</p>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Recent Incidents */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Recent Incidents</CardTitle>
                      <CardDescription>⚠️ No medical details shown. Acknowledge to confirm receipt.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Severity</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.incidents.recentIncidents?.map((incident) => (
                            <TableRow key={incident.id}>
                              <TableCell className="font-medium">{incident.type}</TableCell>
                              <TableCell>{getSeverityBadge(incident.severity)}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{incident.status}</Badge>
                              </TableCell>
                              <TableCell>{formatDate(incident.date)}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => acknowledgeIncident(incident.id, false)}
                                    disabled={acknowledging === incident.id}
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Acknowledge
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="default"
                                    onClick={() => acknowledgeIncident(incident.id, true)}
                                    disabled={acknowledging === incident.id}
                                  >
                                    Request Corrective
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!data.incidents.recentIncidents || data.incidents.recentIncidents.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                                No recent incidents
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Audits Tab */}
              <TabsContent value="audits">
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-blue-600">{data.audits.total}</p>
                        <p className="text-sm text-gray-500">Total Audits</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-green-600">{data.audits.averageScore}%</p>
                        <p className="text-sm text-gray-500">Average Score</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-purple-600">{data.audits.recentAudits?.length || 0}</p>
                        <p className="text-sm text-gray-500">Recent Audits</p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Recent Audits</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Score</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.audits.recentAudits?.map((audit) => (
                            <TableRow key={audit.id}>
                              <TableCell className="font-medium">{audit.type || 'Safety Audit'}</TableCell>
                              <TableCell>
                                <Badge className={audit.score >= 80 ? 'bg-green-500' : audit.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}>
                                  {audit.score}%
                                </Badge>
                              </TableCell>
                              <TableCell>{formatDate(audit.date)}</TableCell>
                            </TableRow>
                          ))}
                          {(!data.audits.recentAudits || data.audits.recentAudits.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                                No recent audits
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Body Map Tab */}
              <TabsContent value="bodymap">
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm">
                    <span className="font-medium text-yellow-800">⚠️ Aggregated injury data.</span>
                    <span className="text-yellow-700 ml-1">No worker names or medical details are displayed.</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Body Part Distribution - Stacked Progress Bars */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Injuries by Body Part</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const entries = Object.entries(data.bodyMap.byBodyPart);
                          const total = entries.reduce((sum, [, val]) => sum + val, 0);
                          return entries.length > 0 ? (
                            <div className="space-y-3">
                              {entries.map(([part, count], idx) => {
                                const percentage = total > 0 ? (count / total) * 100 : 0;
                                return (
                                  <div key={part} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-24 truncate capitalize" title={part}>{part}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: COLORS[idx % COLORS.length]
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-8 text-right">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              No injury data recorded
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    {/* Summary Stats */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Injury Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-red-50 rounded">
                            <span className="font-medium">Total Recorded Injuries</span>
                            <Badge className="bg-red-500">{data.bodyMap.total}</Badge>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p className="font-medium mb-2">Top Affected Areas:</p>
                            <ul className="space-y-1">
                              {Object.entries(data.bodyMap.byBodyPart)
                                .sort(([,a], [,b]) => b - a)
                                .slice(0, 5)
                                .map(([part, count]) => (
                                  <li key={part} className="flex justify-between">
                                    <span className="capitalize">{part.replace(/([A-Z])/g, ' $1').trim()}</span>
                                    <span className="font-medium">{count}</span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GCSubcontractorAnalytics;
